import React from 'react';
import { motion } from 'framer-motion';
import { MonitorSmartphone, ShieldCheck, Cloud, Lightbulb, HeadphonesIcon, Network } from 'lucide-react';

const ServicesGrid = () => {
  const services = [
    { title: "Web Development", description: "High-Performing, Responsive Websites built with modern architectures.", icon: MonitorSmartphone },
    { title: "IT Security", description: "Protecting your data and infrastructure with enterprise-grade encryption.", icon: ShieldCheck },
    { title: "Cloud & Infrastructure", description: "Seamless optimization and management for scalable cloud ecosystems.", icon: Cloud },
    { title: "IT Consulting", description: "Strategic IT Solutions engineered for maximum operational efficiency.", icon: Lightbulb },
    { title: "Customer Support", description: "Fast, reliable, and dedicated technical assistance available 24/7.", icon: HeadphonesIcon },
    { title: "Network Setup", description: "Advanced LAN/WAN configuration, monitoring, and troubleshooting.", icon: Network }
  ];

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1 } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } }
  };

  return (
    <section className="py-24 px-6 max-w-7xl mx-auto">
      
      {/* Section Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-16"
      >
        <span className="text-electric-green font-bold text-sm uppercase tracking-widest mb-2 block">What We Do</span>
        <h2 className="text-4xl md:text-5xl font-extrabold text-white">Our Services</h2>
      </motion.div>

      {/* Grid */}
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {services.map((service, index) => {
          const Icon = service.icon; 
          
          // Identify the 1st, 3rd, and 5th cards (Indexes 0, 2, and 4)
          const isActive = [0, 2, 4].includes(index); 
          
          return (
            <motion.div key={index} variants={cardVariants}>
              {/* Dynamic Card Container */}
              <div className={`relative backdrop-blur-xl rounded-2xl p-8 transition-all duration-300 group overflow-hidden h-full flex flex-col ${
                isActive 
                  ? 'bg-[#1A1F29]/80 border border-white/10' 
                  : 'bg-[#1A1F29]/40 border border-white/5 hover:bg-[#1A1F29]/80 hover:border-white/10'
              }`}>
                
                {/* Dynamic Top Border Glow */}
                <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-electric-green to-transparent transition-opacity duration-500 ${
                  isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`} />

                {/* Dynamic Icon Container */}
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 border ${
                  isActive 
                    ? 'border-electric-green/50 bg-electric-green/10' 
                    : 'border-white/10 bg-white/5 group-hover:border-electric-green/50 group-hover:bg-electric-green/10'
                }`}>
                  <Icon className={`w-7 h-7 transition-colors duration-300 ${
                    isActive ? 'text-electric-green' : 'text-gray-400 group-hover:text-electric-green'
                  }`} strokeWidth={1.5} />
                </div>
                
                {/* Text Content */}
                <h3 className="text-xl font-bold text-white mb-3 tracking-tight">{service.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed flex-grow">{service.description}</p>
                
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
};

export default ServicesGrid;